import {
  type CollisionDetection,
  DndContext,
  type DragEndEvent,
  type DragMoveEvent,
  DragOverlay,
  type DragStartEvent,
  MouseSensor,
  pointerWithin,
  rectIntersection,
  TouchSensor,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  type BoardCard,
  type BoardColumn,
  type BoardColumnView,
  type BoardView,
  formatLocalDate,
  localDateOf,
} from '@hugo/core'
import { useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { ArrowRightLeftIcon, CheckIcon, InboxIcon, SparklesIcon } from 'lucide-react'
import {
  type ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { BoardCardItem, CardFace, MoveMenu } from '@/components/board-card'
import { AddCardButton, BoardComposer } from '@/components/board-composer'
import { askGithubDone, asksGithub } from '@/components/github-done-dialog'
import { SnoozeMenu } from '@/components/snooze-menu'
import { TagFilter } from '@/components/tag-filter'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Kbd } from '@/components/ui/kbd'
import { Skeleton } from '@/components/ui/skeleton'
import { View } from '@/components/view'
import { useWorkspace } from '@/components/workspace'
import { ApiError } from '@/lib/api'
import {
  busyLabel,
  type CardLine,
  COLUMN_LABELS,
  cardLine,
  columnBeside,
  dropPosition,
  findCard,
  focusLabel,
  moveDetail,
  movedTitle,
  moveLocally,
  neighbour,
  positionOf,
  spanLabel,
  TODAY_COLUMNS,
  timeLabel,
} from '@/lib/board'
import { formatDay } from '@/lib/format'
import { GITHUB_DONE } from '@/lib/item-actions'
import { queryKeys, useBoard, useRunTool, useSettings } from '@/lib/items'
import { isTypingTarget, overlayOpen } from '@/lib/keys'
import { userToday } from '@/lib/plan'
import { useUserNow } from '@/lib/session'
import { hasTag, tagsOf } from '@/lib/tags'
import { cn } from '@/lib/utils'

type DropTarget = { column: BoardColumn; cardId: string | null; after: boolean; self?: boolean }

const ARROWS: Record<string, 'up' | 'down' | 'left' | 'right'> = {
  arrowup: 'up',
  k: 'up',
  arrowdown: 'down',
  j: 'down',
  arrowleft: 'left',
  h: 'left',
  arrowright: 'right',
  l: 'right',
}

/** Cards win over the column around them; outside every card, the column under the pointer. */
const collide: CollisionDetection = (args) => {
  const hits = pointerWithin(args)
  const card = hits.find((hit) => String(hit.id).startsWith('card:'))
  if (card) return [card]
  const column = hits.find((hit) => String(hit.id).startsWith('column:'))
  return column ? [column] : rectIntersection(args)
}

/** Move a card: in the cache at once, then on the server; the toast says what else changed. */
function useMoveCard(thisYear: number) {
  const queryClient = useQueryClient()
  const { run } = useRunTool()
  return useCallback(
    async (id: string, column: BoardColumn, position?: number, onPlaced?: () => void) => {
      const before = queryClient.getQueryData<BoardView>(queryKeys.board)
      // A GitHub task dropped on Done asks what GitHub should do, like every done (decision 108).
      const card = before?.columns.flatMap((c) => c.cards).find((c) => c.item.id === id)
      if (column === 'done' && card && asksGithub(card.item)) {
        const github = await askGithubDone(card.item)
        if (github === null) return
        await queryClient.cancelQueries({ queryKey: queryKeys.board })
        if (before)
          queryClient.setQueryData(queryKeys.board, moveLocally(before, id, column, position))
        onPlaced?.()
        await run(
          'item.complete',
          { id, github },
          {
            done: GITHUB_DONE[github],
            failed: 'Could not mark it done',
            onError: () => {
              if (before) queryClient.setQueryData(queryKeys.board, before)
              return false
            },
          },
        )
        return
      }
      if (before) {
        await queryClient.cancelQueries({ queryKey: queryKeys.board })
        queryClient.setQueryData(queryKeys.board, moveLocally(before, id, column, position))
      }
      onPlaced?.()
      await run('item.move', position === undefined ? { id, column } : { id, column, position }, {
        done: (result) => movedTitle(result.column),
        detail: (result) => moveDetail(result, thisYear),
        failed: 'Could not move it',
        onError: () => {
          if (before) queryClient.setQueryData(queryKeys.board, before)
          return false
        },
      })
    },
    [queryClient, run, thisYear],
  )
}

type ColumnProps = {
  column: BoardColumnView
  board: BoardView
  selectedId: string | null
  draggingId: string | null
  target: DropTarget | null
  timeZone: string
  thisYear: number
  lineOf: (card: BoardCard, column: BoardColumn) => CardLine | null
  onSelect: (card: BoardCard) => void
  onOpen: (card: BoardCard) => void
  onMove: (card: BoardCard, column: BoardColumn) => void
  personalTags?: readonly string[]
  /** The "Add a card" composer is open in this column. */
  adding: boolean
  onAdd: (open: boolean) => void
  header?: ReactNode
  className?: string
}

/** The header of a column with days: its time for tasks, what is planned, meetings. */
function TimeLines({ column, thisYear }: { column: BoardColumnView; thisYear: number }) {
  const span = spanLabel(column, thisYear)
  const busy = column.time ? busyLabel(column.time) : null
  return (
    <>
      {span ? <span className="text-xs text-muted-foreground">{span}</span> : null}
      {column.time ? (
        <span className="text-xs tabular-nums" data-testid="column-time">
          {timeLabel(column.time)}
        </span>
      ) : column.id === 'this_week' ? (
        <span className="text-xs text-muted-foreground">No days left this week</span>
      ) : null}
      {busy ? <span className="text-xs text-muted-foreground">{busy}</span> : null}
    </>
  )
}

/** One column: its header and its cards, the user's first, then hugo's own. */
function Column({
  column,
  board,
  selectedId,
  draggingId,
  target,
  timeZone,
  thisYear,
  lineOf,
  onSelect,
  onOpen,
  onMove,
  personalTags,
  adding,
  onAdd,
  header,
  className,
}: ColumnProps) {
  const { setNodeRef } = useDroppable({ id: `column:${column.id}`, data: { column: column.id } })
  const targeted = target?.column === column.id && !target.self
  const ordered = column.id !== 'waiting' && column.id !== 'done'
  const indicator =
    targeted && ordered && draggingId
      ? (dropPosition(
          column,
          draggingId,
          target.cardId ? { cardId: target.cardId, after: target.after } : null,
        ) ?? null)
      : null
  // Waiting and done have no order of the user's, and nothing hugo plans: one list.
  const committed = ordered ? column.cards.filter((card) => card.committed) : column.cards
  const hugos = ordered ? column.cards.filter((card) => !card.committed) : []
  const tomorrow = board.columns.find((c) => c.id === 'tomorrow')
  const tomorrowLabel = tomorrow?.from
    ? `Tomorrow · ${formatDay(tomorrow.from, thisYear)}`
    : undefined
  const item = (card: BoardCard) => (
    <BoardCardItem
      key={card.item.id}
      card={card}
      column={column.id}
      line={lineOf(card, column.id)}
      selected={selectedId === card.item.id}
      timeZone={timeZone}
      thisYear={thisYear}
      onSelect={() => onSelect(card)}
      onOpen={() => onOpen(card)}
      onMove={(to) => onMove(card, to)}
      tomorrowLabel={tomorrowLabel}
      personalTags={personalTags}
    />
  )
  const line = <li key="drop-line" aria-hidden="true" className="h-0.5 rounded-full bg-primary" />
  const rows: ReactNode[] = []
  let k = 0
  for (const card of committed) {
    if (card.item.id !== draggingId) {
      if (k === indicator) rows.push(line)
      k += 1
    }
    rows.push(item(card))
  }
  if (indicator !== null && k <= indicator) rows.push(line)
  const count = column.cards.length
  return (
    <section
      ref={setNodeRef}
      aria-labelledby={`board-${column.id}-heading`}
      data-testid="board-column"
      data-column={column.id}
      data-targeted={targeted || undefined}
      className={cn(
        'flex w-[85vw] max-w-80 shrink-0 snap-center flex-col gap-2 rounded-xl p-2 transition-colors md:w-64',
        targeted && 'bg-primary/5 ring-2 ring-primary/30',
        className,
      )}
    >
      <header className="flex flex-col gap-0.5 px-1">
        <h2
          id={`board-${column.id}-heading`}
          className="flex items-baseline gap-2 text-sm font-semibold"
        >
          {COLUMN_LABELS[column.id]}
          {count > 0 ? (
            <span
              className="text-xs font-normal text-muted-foreground tabular-nums"
              data-testid="column-count"
            >
              {count}
            </span>
          ) : null}
        </h2>
        {header ?? <TimeLines column={column} thisYear={thisYear} />}
      </header>
      <ul className="flex min-h-16 flex-col gap-2" aria-label={COLUMN_LABELS[column.id]}>
        {rows}
        {count === 0 && indicator === null && (column.id === 'done' || draggingId) ? (
          <li className="rounded-lg border border-dashed px-3 py-4 text-center text-xs text-muted-foreground">
            {column.id === 'done' ? 'Nothing done this week yet' : 'Drop cards here'}
          </li>
        ) : null}
        {column.id !== 'done' && !draggingId ? (
          // Where a new card goes: after the cards the user put here, before hugo's own.
          <li>
            {adding ? (
              <BoardComposer column={column.id} timeZone={timeZone} onClose={() => onAdd(false)} />
            ) : (
              <AddCardButton column={column.id} onClick={() => onAdd(true)} />
            )}
          </li>
        ) : null}
        {hugos.length > 0 ? (
          <>
            <li className="flex items-center gap-1 px-1 pt-1 text-xs text-muted-foreground">
              <SparklesIcon aria-hidden="true" className="size-3" />
              Planned by hugo
            </li>
            {hugos.map(item)}
          </>
        ) : null}
      </ul>
    </section>
  )
}

/**
 * The board: open work in columns, left to right toward now: Backlog (by priority), Next week,
 * This week, Tomorrow (the next working day), today's focus work, the rest of today, Now, then
 * Waiting for others and Done this week. The three Today columns are the focus, in a band of
 * their own, scrolled into view. Moving a card (drag, long press, the card's menu, Shift and the
 * arrows) replans everything at once: the toast says what else changed, with Undo. Every number
 * comes from hugo's planner.
 */
export function BoardPage({ tag }: { tag?: string }) {
  const query = useBoard()
  const settings = useSettings()
  const personalTags = settings.data?.personalTags
  const timeZone = settings.data?.timezone ?? 'Europe/Zurich'
  const now = useUserNow(30_000)
  const { today, year } = userToday(now, timeZone)
  const board = query.data
  const { selected, setSelected, openDetails, setCaptureOpen, snoozeOpen, setSnoozeOpen } =
    useWorkspace()
  const move = useMoveCard(year)
  const [moveOpen, setMoveOpen] = useState(false)
  /** The column whose "Add a card" composer is open (one at a time). */
  const [addingIn, setAddingIn] = useState<BoardColumn | null>(null)
  const [dragging, setDragging] = useState<{ card: BoardCard; column: BoardColumn } | null>(null)
  const [target, setTarget] = useState<DropTarget | null>(null)
  const scroller = useRef<HTMLDivElement>(null)
  const todayBand = useRef<HTMLElement>(null)
  const centred = useRef(false)

  /** Every card, as the server placed them (positions count all of them). */
  const allColumns = useMemo(() => board?.columns ?? [], [board])
  /** What shows: with a tag filter only the cards with that tag. */
  const columns = useMemo(
    () =>
      tag === undefined
        ? allColumns
        : allColumns.map((column) => ({
            ...column,
            cards: column.cards.filter((card) => hasTag(card.item, tag)),
          })),
    [allColumns, tag],
  )
  const boardTags = useMemo(
    () => tagsOf(allColumns.flatMap((column) => column.cards.map((card) => card.item))),
    [allColumns],
  )
  const fullColumn = useCallback(
    (id: BoardColumn) => allColumns.find((column) => column.id === id),
    [allColumns],
  )
  const at = selected ? findCard(columns, selected.id) : null
  const current = at ? (at.column.cards[at.index] ?? null) : null

  const dayOf = useCallback(
    (iso: string) => formatLocalDate(localDateOf(Date.parse(iso), timeZone)),
    [timeZone],
  )
  const lineOf = useCallback(
    (card: BoardCard, column: BoardColumn) =>
      cardLine(card, column, { today, thisYear: year, dayOf }),
    [today, year, dayOf],
  )

  // The selection follows the board's data; leaving the page clears it.
  useEffect(() => {
    if (current && current.item !== selected) setSelected(current.item)
  }, [current, selected, setSelected])
  useEffect(() => () => setSelected(null), [setSelected])

  // Today's band in view once the board is there: at the right edge, with as many whole columns
  // before it (the days that lead to today) as fit; on phones its first column.
  useLayoutEffect(() => {
    const box = scroller.current
    const band = todayBand.current
    if (!board || !box || !band || centred.current) return
    centred.current = true
    const pad = Number.parseFloat(getComputedStyle(box).paddingLeft) || 0
    const bandStart = band.offsetLeft - pad
    const wanted = band.offsetLeft + band.offsetWidth + pad - box.clientWidth
    const starts = [...(band.parentElement?.children ?? [])].map(
      (child) => (child as HTMLElement).offsetLeft - pad,
    )
    const start = starts.find((left) => left >= wanted) ?? bandStart
    box.scrollLeft = Math.max(0, Math.min(start, bandStart))
  }, [board])

  const select = useCallback(
    (id: string | null) => {
      const found = id ? findCard(columns, id) : null
      const card = found?.column.cards[found.index]
      if (!card) return
      setSelected(card.item)
      const node = document.querySelector<HTMLElement>(`[data-card-id="${id}"]`)
      node?.querySelector<HTMLElement>('[data-card-open]')?.focus({ preventScroll: true })
      node?.scrollIntoView({ block: 'nearest', inline: 'nearest' })
    },
    [columns, setSelected],
  )

  /**
   * Shift and an arrow: to the next column, or up and down among the user's cards. The card stays
   * selected and in view where it went, so it can move on.
   */
  const moveBy = useCallback(
    (direction: 'up' | 'down' | 'left' | 'right') => {
      if (!at || !current) return
      const id = current.item.id
      const follow = () =>
        requestAnimationFrame(() =>
          document
            .querySelector(`[data-card-id="${id}"]`)
            ?.scrollIntoView({ block: 'nearest', inline: 'nearest' }),
        )
      if (direction === 'left' || direction === 'right') {
        const to = columnBeside(columns, at.column.id, direction)
        if (to) void move(id, to, undefined, follow)
        return
      }
      if (at.column.id === 'waiting' || at.column.id === 'done') return
      const full = fullColumn(at.column.id)
      if (!full) return
      // Past the card above or below among the ones shown (a tag filter hides some); the
      // position counts all the user's cards in the column.
      const mine = at.column.cards.filter((card) => card.committed)
      const index = mine.findIndex((card) => card.item.id === id)
      if (index < 0) {
        // One of hugo's cards: up makes it the user's, last of theirs.
        if (direction === 'up') void move(id, at.column.id, dropPosition(full, id, null), follow)
        return
      }
      const other = mine[index + (direction === 'up' ? -1 : 1)]
      if (!other) return
      const position = dropPosition(full, id, {
        cardId: other.item.id,
        after: direction === 'down',
      })
      void move(id, at.column.id, position, follow)
    },
    [at, current, columns, fullColumn, move],
  )

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.altKey) return
      if (isTypingTarget(event.target) || overlayOpen()) return
      const key = event.key.toLowerCase()
      const direction = ARROWS[key]
      if (direction) {
        if (event.shiftKey) moveBy(direction)
        else select(neighbour(columns, current?.item.id ?? null, direction))
      } else if (key === 'c' && !event.shiftKey) {
        setCaptureOpen(true)
      } else if (key === 'n' && !event.shiftKey) {
        // A new card in the selected card's column (Today without one).
        const column = at?.column.id
        setAddingIn(column && column !== 'done' ? column : 'today')
      } else if (/^[1-9]$/.test(key)) {
        const column = columns[Number(key) - 1]
        const first = column?.cards[0]
        if (!first) return
        select(first.item.id)
      } else if (!current) {
        return
      } else if (key === 'enter') {
        openDetails(current.item.id)
      } else if (key === 'e' && at?.column.id !== 'done') {
        void move(current.item.id, 'done')
      } else if (key === 's' && at?.column.id !== 'done') {
        setSnoozeOpen(true)
      } else if (key === 'm') {
        setMoveOpen(true)
      } else {
        return
      }
      event.preventDefault()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [columns, current, at, select, moveBy, move, openDetails, setCaptureOpen, setSnoozeOpen])

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    // A long press picks a card up on touch screens; a swipe still scrolls.
    useSensor(TouchSensor, { activationConstraint: { delay: 300, tolerance: 8 } }),
  )

  const targetOf = (event: DragMoveEvent | DragEndEvent): DropTarget | null => {
    const over = event.over
    const data = over?.data.current as { column?: BoardColumn; cardId?: string } | undefined
    if (!over || !data?.column) return null
    if (!data.cardId) return { column: data.column, cardId: null, after: false }
    if (data.cardId === event.active.id) {
      return { column: data.column, cardId: null, after: false, self: true }
    }
    const rect = event.active.rect.current.translated
    const middle = rect ? rect.top + rect.height / 2 : 0
    return {
      column: data.column,
      cardId: data.cardId,
      after: middle > over.rect.top + over.rect.height / 2,
    }
  }

  const onDragStart = (event: DragStartEvent) => {
    const found = findCard(columns, String(event.active.id))
    const card = found?.column.cards[found.index]
    if (found && card) {
      setDragging({ card, column: found.column.id })
      setSelected(card.item)
    }
  }
  const onDragMove = (event: DragMoveEvent) => {
    const next = targetOf(event)
    setTarget((prev) =>
      prev?.column === next?.column &&
      prev?.cardId === next?.cardId &&
      prev?.after === next?.after &&
      prev?.self === next?.self
        ? prev
        : next,
    )
  }
  const onDragEnd = (event: DragEndEvent) => {
    const drop = targetOf(event)
    const id = String(event.active.id)
    setDragging(null)
    setTarget(null)
    if (!drop || drop.self) return
    // Positions count every card in the column, shown or not.
    const to = fullColumn(drop.column)
    const from = findCard(allColumns, id)
    if (!to || !from) return
    const position = dropPosition(
      to,
      id,
      drop.cardId ? { cardId: drop.cardId, after: drop.after } : null,
    )
    const unchanged =
      from.column.id === to.id &&
      (position === undefined || position === positionOf(from.column, id))
    if (!unchanged) void move(id, to.id, position)
  }

  const state = query.isPending ? 'loading' : query.isError ? 'error' : 'ready'
  const columnProps = (column: BoardColumnView) => ({
    column,
    board: board as BoardView,
    selectedId: current?.item.id ?? null,
    draggingId: dragging?.card.item.id ?? null,
    target,
    timeZone,
    thisYear: year,
    lineOf,
    onSelect: (card: BoardCard) => select(card.item.id),
    onOpen: (card: BoardCard) => openDetails(card.item.id),
    onMove: (card: BoardCard, to: BoardColumn) => void move(card.item.id, to),
    personalTags,
    adding: addingIn === column.id,
    onAdd: (open: boolean) => setAddingIn(open ? column.id : null),
  })
  const byId = (id: BoardColumn) => columns.find((column) => column.id === id)
  const focus = board ? focusLabel(board.today) : null

  return (
    <View
      name="board"
      state={state}
      title="Board"
      description="Move cards to plan your week; hugo replans everything as you go."
      className="max-w-none"
      actions={
        <>
          {current && at ? (
            <div className="flex items-center gap-2 max-md:hidden" data-testid="board-actions">
              <MoveMenu
                column={at.column.id}
                title={current.item.title}
                tomorrowLabel={
                  byId('tomorrow')?.from
                    ? `Tomorrow · ${formatDay(byId('tomorrow')?.from ?? '', year)}`
                    : undefined
                }
                open={moveOpen}
                onOpenChange={setMoveOpen}
                onMove={(to) => void move(current.item.id, to)}
                trigger={
                  <Button variant="outline" size="sm" aria-label="Move to (M)">
                    <ArrowRightLeftIcon aria-hidden="true" />
                    Move to
                    <Kbd>M</Kbd>
                  </Button>
                }
              />
              {at.column.id !== 'done' ? (
                <>
                  <SnoozeMenu
                    item={current.item}
                    open={snoozeOpen}
                    onOpenChange={setSnoozeOpen}
                    timeZone={timeZone}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    aria-label="Done (E)"
                    onClick={() => void move(current.item.id, 'done')}
                  >
                    <CheckIcon aria-hidden="true" />
                    Done
                    <Kbd>E</Kbd>
                  </Button>
                </>
              ) : null}
            </div>
          ) : null}
          {board && board.inbox > 0 ? (
            <Button
              variant="outline"
              size="sm"
              render={<Link to="/inbox" />}
              data-testid="board-inbox"
            >
              <InboxIcon aria-hidden="true" />
              {board.inbox} to sort
            </Button>
          ) : null}
        </>
      }
    >
      {query.error ? (
        <Alert variant="destructive">
          <AlertTitle>Could not load the board</AlertTitle>
          <AlertDescription>
            {query.error instanceof ApiError
              ? query.error.message
              : 'Something went wrong. Please try again.'}
          </AlertDescription>
        </Alert>
      ) : null}
      {!board ? (
        query.isPending ? (
          <div className="flex gap-3 overflow-hidden">
            {[0, 1, 2, 3].map((n) => (
              <Skeleton key={n} className="h-64 w-72 shrink-0 rounded-xl" />
            ))}
          </div>
        ) : null
      ) : (
        <>
          {boardTags.length > 0 || tag !== undefined ? (
            <TagFilter
              tags={boardTags}
              active={tag}
              personalTags={personalTags}
              link={(option, props) => (
                <Link to="/board" search={option ? { tag: option } : {}} {...props} />
              )}
            />
          ) : null}
          <DndContext
            sensors={sensors}
            collisionDetection={collide}
            // Gentle scrolling near the edges, so the column under the finger can be read.
            autoScroll={{ threshold: { x: 0.1, y: 0.1 }, acceleration: 2 }}
            onDragStart={onDragStart}
            onDragMove={onDragMove}
            onDragEnd={onDragEnd}
            onDragCancel={() => {
              setDragging(null)
              setTarget(null)
            }}
          >
            <div
              ref={scroller}
              className={cn(
                'relative -mx-4 overflow-x-auto px-4 pb-4 [mask-image:linear-gradient(to_right,transparent,#000_1rem,#000_calc(100%-1rem),transparent)] md:-mx-8 md:px-8 md:[mask-image:linear-gradient(to_right,transparent,#000_2rem,#000_calc(100%-2rem),transparent)]',
                // Columns snap on phones, except while a card is dragged: snapping turned every
                // small auto-scroll step into a jump to the next column (found in the iOS Simulator).
                dragging ? 'snap-none' : 'snap-x snap-mandatory md:snap-none',
              )}
              data-testid="board"
            >
              <div className="flex w-max items-start gap-2">
                {(['backlog', 'next_week', 'this_week', 'tomorrow'] as const).map((id) => {
                  const column = byId(id)
                  return column ? <Column key={id} {...columnProps(column)} /> : null
                })}
                <section
                  ref={todayBand}
                  aria-labelledby="board-today-band"
                  data-testid="board-today"
                  className="flex shrink-0 flex-col gap-1 rounded-2xl border bg-muted/50 p-1.5"
                >
                  <div className="sticky left-4 flex w-fit max-w-[calc(85vw-1rem)] flex-wrap items-baseline gap-x-3 gap-y-0.5 px-2.5 pt-1.5 md:static md:max-w-none">
                    <h2 id="board-today-band" className="font-heading text-base font-semibold">
                      Today
                    </h2>
                    <span className="text-xs text-muted-foreground">
                      {formatDay(board.date, year)}
                    </span>
                    <span className="text-xs tabular-nums" data-testid="today-time">
                      {timeLabel(board.today.time)}
                    </span>
                    {busyLabel(board.today.time) ? (
                      <span className="text-xs text-muted-foreground">
                        {busyLabel(board.today.time)}
                      </span>
                    ) : null}
                  </div>
                  <div className="flex items-start gap-1">
                    {TODAY_COLUMNS.map((id) => {
                      const column = byId(id)
                      if (!column) return null
                      return (
                        <Column
                          key={id}
                          {...columnProps(column)}
                          header={
                            id === 'focus' && focus ? (
                              <span
                                className={cn(
                                  'text-xs',
                                  focus.tone === 'attention'
                                    ? 'font-medium text-amber-800 dark:text-amber-300'
                                    : 'text-muted-foreground',
                                )}
                                data-testid="focus-time"
                              >
                                {focus.text}
                              </span>
                            ) : (
                              <span className="text-xs text-muted-foreground">
                                {id === 'now' ? 'What you are on' : 'The rest of today'}
                              </span>
                            )
                          }
                        />
                      )
                    })}
                  </div>
                </section>
                {(['waiting', 'done'] as const).map((id) => {
                  const column = byId(id)
                  return column ? (
                    <Column
                      key={id}
                      {...columnProps(column)}
                      header={
                        <span className="text-xs text-muted-foreground">
                          {id === 'waiting' ? 'By follow-up date' : 'This week'}
                        </span>
                      }
                    />
                  ) : null
                })}
              </div>
            </div>
            <DragOverlay dropAnimation={null}>
              {dragging ? (
                <div className="w-[85vw] max-w-80 md:w-60">
                  <CardFace
                    card={dragging.card}
                    column={dragging.column}
                    line={lineOf(dragging.card, dragging.column)}
                    selected={false}
                    timeZone={timeZone}
                    thisYear={year}
                    personalTags={personalTags}
                    overlay
                  />
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
        </>
      )}
    </View>
  )
}
